import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useWatchBlockNumber } from 'wagmi'

import { BLOCK_HEAD_POLL_INTERVAL_MS } from '../../constants/rpcPolling'
import { useAppChainId } from '../../domain/wallet'
import useDebounce from '../../hooks/useDebounce'
import useIsWindowVisible from '../../hooks/useIsWindowVisible'
import { chainHeadErrorDebug } from '../../utils/debugChainHead'
import { updateBlockNumber } from './actions'

export default function Updater(): null {
  const chainId = useAppChainId()
  const dispatch = useDispatch()
  const windowVisible = useIsWindowVisible()

  const [state, setState] = useState<{ chainId: number | undefined; blockNumber: number | null }>({
    chainId,
    blockNumber: null,
  })

  const blockNumberCallback = useCallback(
    (blockNumber: number) => {
      setState((prev) => {
        if (chainId === prev.chainId) {
          if (typeof prev.blockNumber !== 'number') return { chainId, blockNumber }
          return { chainId, blockNumber: Math.max(blockNumber, prev.blockNumber) }
        }
        return prev
      })
    },
    [chainId],
  )

  useEffect(() => {
    setState({ chainId, blockNumber: null })
  }, [chainId])

  useWatchBlockNumber({
    chainId,
    enabled: Boolean(chainId && windowVisible),
    poll: true,
    pollingInterval: BLOCK_HEAD_POLL_INTERVAL_MS,
    onBlockNumber(blockNumber) {
      blockNumberCallback(Number(blockNumber))
    },
    onError(err) {
      chainHeadErrorDebug('ApplicationUpdater.watchBlockNumber', {
        chainId,
        phase: 'poll',
        message: err instanceof Error ? err.message : String(err),
      })
    },
  })

  const debouncedState = useDebounce(state, 100)

  useEffect(() => {
    if (!debouncedState.chainId || !debouncedState.blockNumber || !windowVisible) return
    dispatch(updateBlockNumber({ chainId: debouncedState.chainId, blockNumber: debouncedState.blockNumber }))
  }, [windowVisible, dispatch, debouncedState.blockNumber, debouncedState.chainId])

  return null
}
